import { useCallback } from "react";
import * as R from "ramda";
import { useQuery, useQueryClient } from "react-query";

import { useCurrentTenant } from "contexts/currentTenant";
import { featuresOfTypeForPoint, fetchTileAt, getPoint, latToTile, lonToTile } from "services/MVT";
import useIsTest from "utils/useIsTest";

interface TileQueryConfigInputs {
  tileType: string;
  tenantId: string | number | null | undefined;
  tileVersion: string | number | null | undefined;
  lat: number | null | undefined;
  lng: number | null | undefined;
  z?: number;
}

interface TileQueryConfigOutput {
  queryKey: (string | number | null | undefined)[];
  queryFn: () => Promise<any>;
  defaultEnabled: boolean;
  tileParams?: { x: number; y: number; z: number };
}

export const getTileQueryConfig = ({
  tileType,
  tenantId,
  tileVersion,
  lat,
  lng,
  z = 16,
}: TileQueryConfigInputs): TileQueryConfigOutput => {
  const defaultEnabled = R.all(R.complement(R.isNil), [tileType, tenantId, tileVersion, lat, lng]);

  let x: number | null = null;
  let y: number | null = null;
  let tileParams: { x: number; y: number; z: number } | undefined;
  let queryFn = () => Promise.resolve(null); // Default to no-op fetch

  if (defaultEnabled) {
    x = lonToTile(lng, z);
    y = latToTile(lat, z);
    tileParams = { x, y, z };
    queryFn = () => fetchTileAt(tileType, tenantId, tileVersion, x!, y!, z);
  }

  const queryKey = ["tile", tileType, tileVersion, x, y, z];
  return { queryKey, queryFn, defaultEnabled, tileParams };
};

export const useTile = (
  tileType: string,
  { lat, lng, z = 16 },
  queryOptions: {
    enabled?: boolean;
    [key: string]: any;
  } = {},
) => {
  const { id, tile_version: tileVersion } = useCurrentTenant();
  const { queryKey, queryFn, defaultEnabled } = getTileQueryConfig({
    tileType,
    tenantId: id,
    tileVersion,
    lat,
    lng,
    z,
  });

  const isEnabled =
    typeof queryOptions.enabled === "boolean"
      ? queryOptions.enabled && defaultEnabled
      : defaultEnabled;

  return useQuery(queryKey, queryFn, {
    staleTime: Infinity,
    cacheTime: Infinity,
    ...queryOptions,
    enabled: isEnabled,
  });
};

export const useIsInBoundary = () => {
  const testing = useIsTest();
  const queryClient = useQueryClient();
  const { id: tenantId, tile_version: tileVersion } = useCurrentTenant();

  const checkCoords = useCallback(
    async (lat: number, lng: number): Promise<boolean> => {
      if (testing) {
        return true; // tests rely on stubbing
      }

      const tileType = "tenants";
      const z = 16;

      const { queryKey, queryFn, defaultEnabled, tileParams } = getTileQueryConfig({
        tileType,
        tenantId,
        tileVersion,
        lat,
        lng,
        z,
      });

      if (!defaultEnabled) {
        return false;
      }

      try {
        const data = await queryClient.fetchQuery(queryKey, queryFn, {
          staleTime: Infinity,
          cacheTime: Infinity,
        });

        if (!data?.tile || !tileParams) {
          return false;
        }

        const { tile } = data;
        const { x, y } = tileParams;
        const point = getPoint(lat, lng);
        const tiles = featuresOfTypeForPoint(tileType, tile, point, x, y, z);
        return !R.isEmpty(tiles);
      } catch (error) {
        console.error("Failed to fetch or process tile for boundary check:", error);
        return false;
      }
    },
    [testing, tenantId, tileVersion, queryClient],
  );

  return checkCoords;
};
